import { activeThreadIdsAtom } from "@/components/threads/hooks/useActiveThreadTracking.ts";
import { InlineMarkdown } from "@/components/markdown/InlineMarkdown.tsx";
import { getThreadPagePath } from "@/components/threads/util.ts";
import { toast } from "@/components/ui/toast.tsx";
import { PIVOT_PATHS } from "@/components/layout/pivotRoutes.ts";
import { useSocket } from "@/contexts/SocketContext.tsx";
import type { ThreadNotifyEventData } from "@/types/socketIo.ts";
import { useAtomValue } from "jotai";
import { Bell } from "lucide-react";
import { useEffect, useEffectEvent } from "react";
import { toast as sonnerToast } from "sonner";
import { useLocation } from "wouter";

/**
 * In-app toast for a thread that is waiting on the user. The dispatcher emits
 * `thread:notify` whenever it fires a push/email notification; here we raise an
 * Okthx toast for an active web user, the in-app complement to push/email.
 * Clicking it opens the thread's page.
 *
 * Only `action_needed` earns a toast. A completion is already carried by the
 * threads list and by the awareness badge, which counts completions unseen
 * since the list was last viewed, so a toast for it is a second copy of news
 * the user can already see. Native has no in-app completion signal to match
 * either, and the two platforms are kept deliberately consistent.
 *
 * Suppressed when the user is already viewing that thread (it's in
 * `activeThreadIds`): the update is already on screen, no toast needed. The
 * socket only reaches connected clients, so this is inherently "active web
 * only" — no extra presence check required.
 */
export const useThreadNotifyToast = () => {
  const { socket } = useSocket();
  const activeThreadIds = useAtomValue(activeThreadIdsAtom);
  const [location, navigate] = useLocation();

  // Reads the current pivot without re-attaching the socket listener on every
  // navigation, which would drop events mid-swap.
  const onNotify = useEffectEvent((data: ThreadNotifyEventData) => {
    if (data.event === "task_completed") return;
    // Already looking at this thread → the change is on screen; don't toast.
    if (activeThreadIds.has(data.threadId)) return;

    // Nor over Tasks. Everything this toast carries is a task okthx is asking
    // about, and the reader is looking at the list those tasks are in — the
    // row already says it needs them. A toast there interrupts the work it is
    // announcing.
    if (location.startsWith(PIVOT_PATHS.tasks)) return;

    toast({
      // Header is the thread title (what it's about); the icon carries the
      // needs-you tier. `title` (e.g. "Okthx needs you") stays on push/email
      // where there's no thread context on screen.
      title: data.threadTitle,
      // The agent's message is markdown; rendered rather than printed so its
      // emphasis shows instead of its asterisks (BIG-8390). Clamped to a few
      // lines so a long message stays toast-sized.
      description: (
        <span className="line-clamp-3 whitespace-pre-line">
          <InlineMarkdown paragraphs links={false}>
            {data.summary}
          </InlineMarkdown>
        </span>
      ),
      Icon: Bell,
      iconClassName: "text-amber-600",
      actionStatsigName: "thread-notify-toast",
      // The whole toast is the click target (no separate "View" button).
      onToastClick: (id) => {
        navigate(getThreadPagePath(data.threadId));
        sonnerToast.dismiss(id);
      },
      dismissible: true,
    });
  });

  useEffect(() => {
    if (!socket) return;
    socket.on("thread:notify", onNotify);
    return () => {
      socket.off("thread:notify", onNotify);
    };
  }, [socket]);
};
